import React, { useEffect, useState } from 'react';
import { notifyError } from "../../../utils/tostfy";
import MasterSuccessModal from '../MasterSuccessModal';
import { handleApiCall } from '../../../apis';
import { endpoint } from '../../../utils/endpoints';
import PermissionGate from '../../../Components/PermissionGate/PermissionGate';
import { MODULES } from '../../../utils/permissions';
import { usePermissions } from '../../../hooks/usePermissions';

const AwardMaster = () => {
    const { can, profileLoaded } = usePermissions();
    const canEditMaster = profileLoaded && can(MODULES.MASTER, 'edit');
    const [awardAmount, setAwardAmount] = useState('');
    const [amountError, setAmountError] = useState('');
    const [loadingAward, setLoadingAward] = useState(true);
    const [savingAward, setSavingAward] = useState(false);
    const [successModal, setSuccessModal] = useState({ open: false, message: '' });
    const openMasterSuccess = (message) =>
        setSuccessModal({ open: true, message: message || 'Saved successfully' });
    const closeMasterSuccess = () => setSuccessModal({ open: false, message: '' });

    const validateAmount = (value) => {
        const v = `${value ?? ''}`.trim();
        if (!v) return 'Award amount is required';
        if (!/^\d+(\.\d{1,2})?$/.test(v)) return 'Enter a valid amount (up to 2 decimals)';
        const n = Number(v);
        if (Number.isNaN(n) || n < 0) return 'Amount must be 0 or greater';
        if (n > 500) return 'Amount can not be greater than 500';
        return '';
    };

    const extractAwardAmount = (data) => {
        if (data == null) return '';
        if (typeof data === 'number' || typeof data === 'string') return `${data}`;
        if (typeof data === 'object') {
            if (data.amount != null) return `${data.amount}`;
            if (data.award != null) return `${data.award}`;
            if (data.data?.amount != null) return `${data.data.amount}`;
            if (Array.isArray(data) && data[0]?.amount != null) return `${data[0].amount}`;
        }
        return '';
    };

    const fetchAward = async () => {
        setLoadingAward(true);
        try {
            const response = await handleApiCall('get', endpoint.getAward, {});
            if (response?.data?.code === 200) {
                const d = response?.data?.data;
                setAwardAmount(extractAwardAmount(d));
            } else {
                const errMsg =
                    response?.response?.data?.errorMessage ||
                    response?.response?.data?.message ||
                    response?.data?.message ||
                    'Failed to fetch award amount';
                notifyError(errMsg);
            }
        } catch {
            notifyError('Failed to fetch award amount');
        } finally {
            setLoadingAward(false);
        }
    };

    useEffect(() => {
        fetchAward();
    }, []);

    const handleSaveAward = async () => {
        const err = validateAmount(awardAmount);
        setAmountError(err);
        if (err) return;

        setSavingAward(true);
        try {
            const response = await handleApiCall('post', endpoint.updateAward, {
                amount: Number(awardAmount),
            });
            if (response?.data?.code === 200) {
                openMasterSuccess(response?.data?.message || 'Award amount updated successfully');
                return;
            }
            const errMsg =
                response?.response?.data?.errorMessage ||
                response?.response?.data?.message ||
                response?.data?.message ||
                'Failed to update award amount';
            notifyError(errMsg);
        } catch {
            notifyError('Something went wrong. Please try again.');
        } finally {
            setSavingAward(false);
        }
    };

    return (
        <>
            <div className="md:mt-7 mt-3 bg-white grid md:grid-cols-4 p-4 shadow-sm">
                <div className='form-group'>
                    <label className='font-UbuntuMedium mb-1'>Award Price</label>
                    <input
                        className="form-control"
                        inputMode='decimal'
                        value={awardAmount}
                        maxLength={3}
                        onChange={(e) => {
                            const cleaned = e.target.value.replace(/[^\d.]/g, '');
                            const firstDot = cleaned.indexOf('.');
                            const normalized =
                                firstDot === -1
                                    ? cleaned
                                    : cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
                            const [intPart, decPart = ''] = normalized.split('.');
                            const finalVal = decPart ? `${intPart}.${decPart.slice(0, 2)}` : intPart;
                            setAwardAmount(finalVal);
                            if (amountError) setAmountError('');
                        }}
                        onBlur={() => setAmountError(validateAmount(awardAmount))}
                        disabled={loadingAward || savingAward || !canEditMaster}
                    />
                    {amountError && (
                        <p className='text-danger small mt-1 mb-0'>{amountError}</p>
                    )}
                </div>
            </div>
            <PermissionGate module={MODULES.MASTER} action="edit">
                <button
                    type='button'
                    className='btn btn-danger md:min-w-40 mt-4 !py-2.5 max-[767px]:!px-10'
                    onClick={handleSaveAward}
                    disabled={loadingAward || savingAward}
                >
                    {savingAward ? 'Saving...' : 'Save'}
                </button>
            </PermissionGate>
            <MasterSuccessModal
                open={successModal.open}
                message={successModal.message}
                onClose={closeMasterSuccess}
            />
            </>
    );
};

export default AwardMaster;